import React from 'react';
import { Cake } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUpcomingBirthdays } from '../../hooks';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { UpcomingBirthday } from '../../types';

interface UpcomingBirthdaysCardProps {
  days?: number;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function ProximityBadge({ days, t }: { days: number; t: (key: string, options?: Record<string, unknown>) => string }) {
  if (days === 0) {
    return (
      <Badge variant="success" className="whitespace-nowrap">
        {t('birthdays.today')}
      </Badge>
    );
  }
  if (days === 1) {
    return (
      <Badge variant="warning" className="whitespace-nowrap">
        {t('birthdays.tomorrow')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="whitespace-nowrap">
      {t('birthdays.inDays', { count: days })}
    </Badge>
  );
}

function BirthdayRow({
  birthday,
  t,
  tCommon,
}: {
  birthday: UpcomingBirthday;
  t: (key: string, options?: Record<string, unknown>) => string;
  tCommon: (key: string) => string;
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-8 w-8">
        {birthday.photoUrl && (
          <AvatarImage src={birthday.photoUrl} alt={birthday.displayName} />
        )}
        <AvatarFallback className="text-xs">
          {getInitials(birthday.displayName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{birthday.displayName}</p>
        <p className="text-xs text-muted-foreground">
          {tCommon(`months.${birthday.birthMonth}`)} {birthday.birthDay}
        </p>
      </div>
      <ProximityBadge days={birthday.daysUntilBirthday} t={t} />
    </div>
  );
}

export const UpcomingBirthdaysCard: React.FC<UpcomingBirthdaysCardProps> = ({ days = 30 }) => {
  const { t } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { data: birthdays = [], isLoading } = useUpcomingBirthdays(days);

  if (!isLoading && birthdays.length === 0) {
    return null;
  }

  return (
    <Card data-testid="upcoming-birthdays-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Cake className="h-5 w-5" />
          {t('birthdays.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-6 text-muted-foreground">{tCommon('loading')}</div>
        ) : (
          <div className="space-y-3">
            {birthdays.map((b) => (
              <BirthdayRow key={b.id} birthday={b} t={t} tCommon={tCommon} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
